import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, Typography, Button, Box, Link } from '@mui/material';
import { IS_IN_WEBVIEW, COOKIE_POLICY_URL } from '../constants';
import { useCookieConsent } from '../hooks/useCookieConsent';
import CookiePreferencesModal from './CookiePreferencesModal';

const useStyles = makeStyles((theme) => ({
  banner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    padding: theme.spacing(2),
  },
  container: {
    maxWidth: 960,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(2),
    flexShrink: 0,
    flexDirection: 'column',
  },
  button: {
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: '2px solid black',
      outlineOffset: 2,
    },
  },
  link: {
    color: 'inherit',
    textDecorationColor: 'inherit',
  },
}));

const CookieConsentBanner = () => {
  const classes = useStyles();
  const consent = useCookieConsent();
  const [showModal, setShowModal] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    // Focus the banner when it appears for keyboard accessibility
    if (!consent.hasConsent && !showModal && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [consent.hasConsent, showModal]);

  return (
    <>
      {!IS_IN_WEBVIEW && !consent.hasConsent && !showModal && (
        <Paper
          elevation={24}
          className={classes.banner}
          ref={bannerRef}
          tabIndex={-1}
          role="region"
          aria-label="Cookie consent banner"
        >
          <Box className={classes.container}>
            <Box className={classes.content}>
              <Typography variant="h6" component="h2" gutterBottom>
                Cookie Preferences
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                We use cookies on our site to enhance your experience, improve
                our site, and to deliver content and ads that are relevant to
                you. By using this site and selecting your cookie preferences,
                you agree to our use of cookies in accordance with our{' '}
                <Link
                  href={COOKIE_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.link}
                >
                  Cookie Policy
                </Link>
                .
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You can choose to accept all cookies by selecting 'Accept All',
                accept only essential cookies by selecting 'Decline All', or
                manage them individually by selecting 'Manage Preferences'.
              </Typography>
            </Box>

            <Box className={classes.actions}>
              <Button
                variant="contained"
                onClick={consent.acceptAll}
                className={classes.button}
              >
                Accept All
              </Button>
              <Button
                variant="outlined"
                onClick={consent.rejectAll}
                className={classes.button}
              >
                Decline All
              </Button>
              <Button
                onClick={() => setShowModal(true)}
                className={classes.button}
              >
                Manage Preferences
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <CookiePreferencesModal
        open={showModal}
        onClose={() => setShowModal(false)}
        consent={consent}
      />
    </>
  );
};

export default CookieConsentBanner;
